import { memo } from 'react';
import { TDbOrderHistory } from '@shared/types/db/order_history';
import { useRouter } from 'next/router';
import { DbCloudWallet, DbCloudWalletUserInfo } from '@shared/types/db/user';
import { AggregatorResponse } from '@shared/types/services/aggregator-response';
import ImageWithFallback from '@/components/shared/ImageWithFallback';
import { Button } from '@/components/ui/button';
import { useSwap } from '@/contexts/SwapProvider';
import classNames from '@/lib/misc/classNames';
import { toSpecialFixed } from '@/lib/misc/utils';
import { useIsMobile } from '@/hooks/utilities/use-mobile';
import useAppDetails from '@/hooks/utilities/useAppDetails';
const BuySellPeople = memo(({
  order
}: {
  order: TDbOrderHistory & DbCloudWallet & DbCloudWalletUserInfo;
}) => {
  const {
    resetAssets
  } = useSwap();
  const isMobile = useIsMobile();
  const {
    setMobileMode,
    setMobileModeMetadata
  } = useAppDetails();
  function shortName(str: string) {
    const maxLength = 13; // Set the desired maximum length
    if (str.length <= maxLength) {
      return str; // No need to truncate if the string is already short
    }
    return str.substring(0, maxLength) + '...';
  }
  function getAssetInfo() {
    let isBuy: boolean;
    let isSell: boolean;
    let isSwap: boolean;
    let inUsdValue: number;
    let tokenInInfo: {
      contract_ticker_symbol: string;
      network: number;
      contract_address: string;
      logo_url: string;
    };
    let tokenOutInfo: {
      contract_ticker_symbol: string;
      network: number;
      contract_address: string;
      logo_url: string;
    };
    let error: string;
    try {
      const tokenPrices = order?.token_prices;
      const tokenOutPrice = order?.token_out_prices?.[0];
      const tokensIn = tokenPrices?.tokensIn?.[0];
      const tokensOut = tokenPrices?.tokensOut?.[0];
      const quotingEngineOrder = order?.result?.decodedResponse?.quote;
      const {
        fees
      } = quotingEngineOrder as AggregatorResponse;
      const {
        grossUsd
      } = fees;
      inUsdValue = grossUsd;
      const {
        contract_ticker_symbol: inSymbol,
        network: inNetwork,
        contract_address: inContractAddress,
        logo_urls: inLogoUrls
      } = tokensIn;
      const {
        contract_ticker_symbol: outSymbol,
        network: outNetwork,
        contract_address: outContractAddress,
        logo_urls: outLogoUrls
      } = tokensOut;
      tokenInInfo = {
        contract_ticker_symbol: inSymbol,
        network: inNetwork,
        contract_address: inContractAddress,
        logo_url: inLogoUrls?.token_logo_url
      };
      tokenOutInfo = {
        contract_ticker_symbol: outSymbol,
        network: outNetwork,
        contract_address: outContractAddress,
        logo_url: outLogoUrls?.token_logo_url || tokenOutPrice?.token?.info?.imageSmallUrl || tokenOutPrice?.token?.info?.imageThumbUrl
      };
      const symbols = ['eth', 'avax', 'bnb', 'usdc', 'pol', 'usdt', 'dai', 'sol'];
      isBuy = symbols.includes(inSymbol.toLowerCase());
      isSell = symbols.includes(outSymbol.toLowerCase());
      if (isBuy && isSell) {
        if (inSymbol?.toLowerCase() === 'usdc' || inSymbol?.toLowerCase() === 'usdt') {
          isBuy = true;
          isSell = false;
        }
      }
      isSwap = !isBuy && !isSell;
    } catch (e) {
      console.error('getAssetInfo error', e);
      error = e.message;
    }
    return {
      tokenInInfo,
      tokenOutInfo,
      isBuy,
      isSell,
      error,
      inUsdValue,
      isSwap
    };
  }
  const {
    tokenInInfo,
    tokenOutInfo,
    isBuy,
    isSell,
    error,
    inUsdValue
  } = getAssetInfo();
  const router = useRouter();
  if (error) {
    return <></>;
  }
  return <div onClick={() => {
    resetAssets();
    const tokenAddress = isSell ? tokenInInfo?.contract_address : tokenOutInfo?.contract_address;
    const networkId = isSell ? tokenInInfo?.network : tokenOutInfo?.network;
    if (isMobile) {
      setMobileMode('asset');
      setMobileModeMetadata({
        address: tokenAddress,
        networkId,
        prev_m_mode: 'feed'
      });
      router.push(`/home?m_mode=asset&address=${tokenAddress}&networkId=${networkId}&prev_m_mode=feed`, undefined, {
        shallow: true
      });
    } else {
      router.push(`/asset?address=${tokenAddress}&networkId=${networkId}&tradeSide=buy`);
    }
  }}>
        <div className='flex justify-between items-center w-full h-[70px]'>
          <div className='flex items-center'>
            {' '}
            <ImageWithFallback src={'/static/genie/' + order.pfp} className='w-[34px] h-[34px] ml-2 rounded-full' alt='order' width={34} height={34} />
            <div className='flex flex-col items-start justify-start ml-4'>
              <p className='text-[16px] flex items-center text-genius-cream'>
                {shortName(order.first_name)}
                <span className='text-genius-cream/50 text-[10px] ml-2'>
                  {(() => {
                const result = new Date(order.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric'
                });
                return result;
              })()}{' '}
                </span>
              </p>
              <div className='flex items-center text-[12px] space-x-1 flex-wrap '>
                {' '}
                <div className='bg-genius-blue px-1 py-1.5 flex items-center gap-x-1 rounded-md'>
                  <p className={classNames('text-genius-cream  ', isSell ? 'text-red-400' : isBuy ? 'text-green-400' : 'text-genius-cream')}>
                    <p>{isSell ? 'SOLD' : isBuy ? 'BOUGHT' : 'SWAPPED'}</p>
                  </p>
                  <p className='text-genius-cream'>
                    {' '}
                    ${toSpecialFixed(inUsdValue?.toString(), 0).value}
                  </p>
                </div>
                <p>
                  {' '}
                  {isSell ? tokenInInfo?.contract_ticker_symbol : isBuy ? tokenOutInfo?.contract_ticker_symbol : `${tokenInInfo?.contract_ticker_symbol} for ${tokenOutInfo?.contract_ticker_symbol}`}
                </p>
                <ImageWithFallback src={isSell ? tokenInInfo?.logo_url : tokenOutInfo?.logo_url} alt={isSell ? tokenInInfo?.contract_ticker_symbol : tokenOutInfo?.contract_ticker_symbol} width={16} height={16} className='rounded-full mx-1' />
              </div>
            </div>
          </div>

          <div className='flex items-center justify-end space-x-1'>
            <Button className={'w-[71px] h-[36px] text-[14px] text-genius-cream rounded-[8px] bg-genius-blue hover:bg-genius-pink hover:text-genius-blue transition-colors'}>
              Trade
            </Button>
          </div>
        </div>
      </div>;
});
export default BuySellPeople;
BuySellPeople.displayName = 'BuySellPeople';